import type { AssetProfile } from '@akem/shared';
import {
    Box,
    Chip,
    Link,
    Stack,
    Typography,
    useTheme
} from '@mui/material';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import type { WidgetProps } from '../types/index.js';

interface Supplier {
  name: string;
  component?: string;
  risk?: string;
  location?: string;
  criticality?: string;
  ticker?: string;
}

interface SupplyChainResearch {
  suppliers: Supplier[];
  risks: string[];
  logistics: string[];
  summary?: string;
}

export const SupplyChainWidget: React.FC<WidgetProps> = ({ widget }) => {
  const theme = useTheme();
  const profile = widget.inputs.find((i) => i.id === 'profile')?.value as AssetProfile | undefined;
  const research = profile?.research?.supplyChain as SupplyChainResearch | undefined;

  if (!research || (!research.suppliers?.length && !research.risks?.length && !research.logistics?.length)) {
    return (
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography variant="body2" color="text.secondary">
          No supply chain data available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', p: 2 }}>
      {research.summary && (
        <Typography variant="body2" color="text.secondary" paragraph>
          {research.summary}
        </Typography>
      )}

      {(research.risks?.length ?? 0) > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight="600" gutterBottom>
            Key Vulnerabilities
          </Typography>
          <Stack spacing={1}>
            {research.risks.map((risk, idx) => (
              <Typography key={idx} variant="body2" sx={{ display: 'flex', gap: 1 }}>
                <span style={{ color: theme.palette.error.main }}>•</span> {risk}
              </Typography>
            ))}
          </Stack>
        </Box>
      )}

      {(research.logistics?.length ?? 0) > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight="600" gutterBottom>
            Logistics & Distribution
          </Typography>
          <Stack spacing={1}>
            {research.logistics.map((item, idx) => (
              <Typography key={idx} variant="body2" sx={{ display: 'flex', gap: 1 }}>
                <span style={{ color: theme.palette.info.main }}>•</span> {item}
              </Typography>
            ))}
          </Stack>
        </Box>
      )}

      {(research.suppliers?.length ?? 0) > 0 && (
        <Box>
          <Typography variant="subtitle2" fontWeight="600" gutterBottom>
            Key Suppliers
          </Typography>
          <Stack spacing={2}>
            {research.suppliers.map((supplier, idx) => (
              <Box key={idx} sx={{ borderBottom: idx < research.suppliers.length - 1 ? '1px solid' : 'none', borderColor: 'divider', pb: idx < research.suppliers.length - 1 ? 2 : 0 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="subtitle2" fontWeight="600">
                      {supplier.ticker ? (
                        <Link
                          component={RouterLink}
                          to={`/profiles/${encodeURIComponent(supplier.ticker)}`}
                          underline="hover"
                          color="inherit"
                        >
                          {supplier.name} ({supplier.ticker})
                        </Link>
                      ) : (
                        supplier.name
                      )}
                    </Typography>
                    {supplier.component && (
                      <Typography variant="caption" color="text.secondary">
                        Provides: {supplier.component}
                      </Typography>
                    )}
                  </Box>
                  {supplier.criticality && (
                    <Chip 
                      label={supplier.criticality} 
                      size="small" 
                      color={supplier.criticality.toLowerCase().includes('high') ? 'warning' : 'default'} 
                      variant="outlined" 
                    />
                  )}
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" gap={0.5}>
                  {supplier.location && (
                    <Chip label={supplier.location} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                  )}
                  {supplier.risk && (
                    <Chip label={`Risk: ${supplier.risk}`} size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                  )}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};
